import {Fragment} from 'react'
import {Link, useMatches, useRouteLoaderData} from '@remix-run/react'
import {slugToText, textToSlug} from '@roundforest/text-transforms-commons'
import clsx from 'clsx'
import SearchBoxRedesign from '~/components/search-combo/search-box-redesign'
import {useReportWidgetClick, useReportWidgetSeen} from '~/hooks/analytic-hooks'
import {IconRightArrowLightWeight} from '~/icons/home-page/brg-icon-app-logo-thick'
import {getTranslation} from '~/localization/translation'
import type {Matches, RootLoaderResponse} from '~/types'
import {getDomainConfig} from '~/domains'

const HomeCategoriesHeader = () => {
  const {language, domain, locale} = useRouteLoaderData('root') as RootLoaderResponse
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)
  const translations = getTranslation(language)
  const matches = useMatches() as Matches[]
  const [location] = matches.filter((match) => match.handle && match.handle?.breadcrumb)
  const categoryTitle = location?.params[location?.handle?.breadcrumb]
  const breadcrumbs = location?.pathname
    .split('/')
    .slice(0, -1)
    .filter((item) => item !== '')
    .map((pathName, index, pathList) => ({
      name: pathName,
      path: `/${pathList.slice(0, index + 1).join('/')}`,
    }))

  const sendWidgetClickEvent = useReportWidgetClick()
  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'home-page-top-rated',
    widget_place: 'top',
  })

  return (
    <header
      className={clsx(
        'flex justify-center',
        stylesBydomain.HOMEPAGE_HEADER_BG,
        stylesBydomain.HOMEPAGE_HEADER_TEXT,
        'homepage_header_bg homepage_header_text',
      )}
      ref={widgetRef}
    >
      <div
        id="wrapper"
        className={clsx(stylesBydomain.UTILS_MAIN_CONTAINER, 'pt-7', 'utils_main_container')}
        data-testid="home-page-header"
      >
        <div className="flex h-10 flex-row items-center">
          {breadcrumbs?.length > 0 &&
            breadcrumbs.map((breadcrumb, index) => (
              <Fragment key={`${breadcrumb.name}-${index}`}>
                {index === 0 ? (
                  <Link
                    reloadDocument
                    to={`${breadcrumb.path}`}
                    data-testid="top-categories-breadcrumb"
                    className="flex flex-row items-center gap-2 text-sm"
                    aria-label={`go to ${translations.Home.allCategories}`}
                  >
                    <IconRightArrowLightWeight
                      className={clsx(
                        'h-5 w-5 rotate-180 stroke-2',
                        stylesBydomain.HOMEPAGE_HEADER_ICON_STROKE,
                        'homepage_header_icon_stroke',
                      )}
                    />
                    {translations.Home.allCategories}
                  </Link>
                ) : (
                  <>
                    <p className="mx-3">/</p>
                    <Link
                      reloadDocument
                      to={`${breadcrumb.path}`}
                      data-testid="l1-categories-breadcrumb"
                      aria-label={`go to ${slugToText(breadcrumb.name)}`}
                      className="text-sm"
                    >
                      {slugToText(breadcrumb.name)}
                    </Link>
                  </>
                )}
              </Fragment>
            ))}
        </div>
        <div className="mt-3 flex items-end justify-center text-center text-[40px] font-bold">
          {categoryTitle ? (
            <h1 className="text-center text-5xl capitalize" data-testid="category-title">
              {slugToText(categoryTitle)}
            </h1>
          ) : (
            <h1
              className={clsx(
                "text-center text-5xl after:content-['.']",
                stylesBydomain.HOMEPAGE_HEADER_TITLE_DOT,
                'homepage_header_title_dot',
              )}
              data-testid="home-page-title"
            >
              {translations.Home.homePageTitle}
            </h1>
          )}
        </div>
        <h2 data-testid="home-page-subtitle" className="mb-14 mt-4 flex justify-center text-center tracking-wide">
          {translations.Home?.homePageSubTitle}
        </h2>
        <SearchBoxRedesign standalone={true} />
        <div className="no-scrollbar mt-2 w-full overflow-x-auto overflow-ellipsis whitespace-nowrap">
          <div data-testid="top-rated-links" className="mt-6 items-center justify-center pb-10 text-sm">
            {translations.Home?.topRated}:
            {translations.Home.topRatedData.map((category, index) => {
              return (
                <Link
                  reloadDocument
                  className={clsx(
                    'mx-2 rounded-full border border-solid px-4 py-1 text-xs',
                    stylesBydomain.HOMEPAGE_TOPRATED_BUTTON,
                    'homepage_toprated_button',
                  )}
                  key={`${category.title}-${index}`}
                  to={`/${textToSlug(category.title)}`}
                  data-testid={`link-to-${textToSlug(category.title)}`}
                  onClick={() =>
                    sendWidgetClickEvent({
                      trigger_type: `${index + 1}-${category.title}`,
                      widget_name: 'home-page-top-rated',
                      widget_place: 'top',
                    })
                  }
                >
                  {category.title}
                </Link>
              )
            })}
          </div>
        </div>
      </div>
    </header>
  )
}

export default HomeCategoriesHeader
